import {
  AdminLayout,
  useLocalStorage,
  useSource,
  useTranslation,
} from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { AdminToolbar, FixedDescription } from "@opendash/ui";
import { Alert, Card, Checkbox, Table, Tag } from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useSourceWithChildren } from "../../features/schedules";
import { ScheduleSourceDetail } from "../../features/schedules/components/overview/ScheduleSourceDetail";
import {
  isScheduleOverdue,
  useScheduleData,
} from "../../features/schedules/hooks/useScheduleData";
import { Schedule_Template } from "../../js/parsegenerated";

export const ScheduleOverviewPage = observer(() => {
  const t = useTranslation();
  const [source] = useSource();
  const validIds = useSourceWithChildren(source?.id);
  const { scheduleData } = useScheduleData();
  const [includeChildren, setIncludeChildren] = useLocalStorage<boolean>(
    "openservice:show-children-in-schedule-table",
    false
  );
  const [showInfo, setShowInfo] = useLocalStorage<boolean>(
    "openservice:schedule-overview-info",
    true
  );

  function filterByRootSource(sources: Source[]) {
    if (!source?.id) return true;
    return includeChildren
      ? sources.some((src) => validIds.includes(src.id))
      : sources.some((src) => src.id === source.id);
  }

  return (
    <AdminLayout contentPadding>
      <AdminToolbar
        title={t("maintenance:schedule.overview.title", {
          source: source?.name,
        })}
        children={
          <>
            {showInfo && (
              <Alert
                closable
                showIcon
                onClose={() => setShowInfo(false)}
                message={t("maintenance:schedule.overview.info.title")}
                description={t(
                  "maintenance:schedule.overview.info.description"
                )}
              />
            )}
          </>
        }
        actions={
          <Checkbox
            checked={includeChildren}
            onChange={(e) => setIncludeChildren(e.target.checked)}
          >
            {t("maintenance:schedule.source.actions.filter.children")}
          </Checkbox>
        }
      />
      <FixedDescription>
        <Card>
          <Table
            columns={[
              {
                title: t("maintenance:schedule.overview.table.col.name"),
                dataIndex: "template",
                render: (value: Schedule_Template) => value.get("title"),
              },
              {
                title: t("maintenance:schedule.overview.table.col.sources"),
                dataIndex: "sources",
                render: (value: Source[]) =>
                  value
                    ?.map((source) => source.name)
                    .sort((a, b) => a.localeCompare(b))
                    .join(", "),
              },
              {
                title: t(
                  "maintenance:schedule.overview.table.col.nextExecution"
                ),
                dataIndex: "nextExecution",
                render: (value: Date, row) => (
                  <Tag color={isScheduleOverdue(row) ? "error" : "default"}>
                    {dayjs(value).format("DD.MM.YYYY")}
                  </Tag>
                ),
              },
            ]}
            dataSource={scheduleData?.filter((item) =>
              filterByRootSource(item.sources as Source[])
            )}
            expandable={{
              expandedRowRender: (record) => (
                <ScheduleSourceDetail data={record} />
              ),
            }}
            rowKey={(row) => row.template.id}
          />
        </Card>
      </FixedDescription>
    </AdminLayout>
  );
});
